import { destroy, flow, getRoot, types } from 'mobx-state-tree';
import { NewNews as News } from 'http-requests';
import {
  NewsDailyStatEntryDTO,
  NewsFeedDTO,
  NewsStatisticsDTO,
} from 'http-requests/news';
import { RequestStateModel } from '../../general-models';
import { IUserStore } from '../../userStore';

export const NewsFeedBaseModel = types
  .model('NewsFeedBase', {
    id: types.identifier,
    subject: types.optional(types.string, ''),
    body: types.optional(types.string, ''),
    state: types.optional(
      types.enumeration(['draft', 'scheduled', 'published'] as const),
      'published'
    ),
    author: types.maybeNull(
      types.model({
        id: types.optional(types.maybeNull(types.string), null),
        provider: types.optional(types.string, ''),
        str_name: types.optional(types.string, ''),
        provider_name: types.optional(types.maybeNull(types.string), null),
        is_super: types.optional(types.boolean, false),
        display_organization: types.optional(types.boolean, false),
      })
    ),
    publishAt: types.optional(types.maybeNull(types.string), null),
    expiresAt: types.optional(types.maybeNull(types.string), null),
    createdAt: types.optional(types.string, ''),
    updatedAt: types.optional(types.string, ''),
    forWorkers: types.optional(types.boolean, false),
    forTenants: types.optional(types.boolean, false),
    forProviders: types.optional(types.boolean, false),
    forContractors: types.optional(types.boolean, false),
    departmentOnly: types.optional(types.boolean, false),
    photos: types.optional(types.array(types.frozen()), []),
    files: types.optional(types.array(types.frozen()), []),
    statistics: types.optional(
      types.maybeNull(types.frozen<NewsStatisticsDTO>()),
      null
    ),
    dailyStatistics: types.optional(
      types.array(types.frozen<NewsDailyStatEntryDTO>()),
      []
    ),
    dailyStatisticsStatus: types.optional(RequestStateModel, {}),
    changeStateStatus: types.optional(RequestStateModel, {}),
    deleteStatus: types.optional(RequestStateModel, {}),
  })
  .views((self) => ({
    get uiFlags() {
      const userStore = getRoot<{ user: IUserStore }>(self).user;
      const canEdit = self.author?.provider === userStore.providerId;
      const canTogglePublish = canEdit || userStore.auth.is_super;

      return {
        canEdit,
        canTogglePublish,
        canDelete: canEdit,
        showStatistics:
          canTogglePublish && self.state === 'published' && !!self.statistics,
      };
    },
    get stateFlags() {
      return {
        isPublished: self.state === 'published',
        isScheduled: self.state === 'scheduled',
        isDraft: self.state === 'draft',
      };
    },
  }))
  .actions((self) => ({
    getDailyFeedStatistics: flow(function* () {
      if (self.dailyStatisticsStatus.pending) {
        return;
      }

      try {
        self.dailyStatisticsStatus.loadingStates();
        const response: { results: NewsDailyStatEntryDTO[] } =
          yield News.GetDailyFeedStatistics({
            news_id: self.id,
            date_from: self.createdAt,
            date_till: new Date().toISOString(),
          });
        self.dailyStatistics.replace(response.results);
        self.dailyStatisticsStatus.successStates();
      } catch (error) {
        self.dailyStatisticsStatus.errorStates();
        console.error(error);
      } finally {
        self.dailyStatisticsStatus.finallyStates();
      }
    }),
    changeState: flow(function* (state: NewsFeedDTO['state']) {
      try {
        self.changeStateStatus.loadingStates();

        const dto: NewsFeedDTO = yield News.GetNewsById(self.id);

        yield News.UpdateNews(self.id, {
          ...dto,
          state,
          // При публикации publish_at сбрасывается, чтобы не нарушать порядок.
          // Иначе запланированные новости показываются вверху до выбранной даты,
          // а старые остаются на своих местах, вместо подъема наверх
          ...(state === 'published' ? { publish_at: null } : {}),
        });

        const root = getRoot<{
          news: {
            fetchNewsCounts: () => void;
            removeItem: (id: string, state: NewsFeedDTO['state']) => void;
          };
        }>(self);

        const currentState = self.state;
        void root.news.fetchNewsCounts();
        root.news.removeItem(self.id, currentState);
      } catch (error) {
        self.changeStateStatus.errorStates();
        self.changeStateStatus.finallyStates();
        console.error(error);

        return Promise.reject(error);
      }
    }),
    delete: flow(function* () {
      try {
        self.deleteStatus.loadingStates();

        yield News.DeleteNews(self.id);

        const root = getRoot<{
          news: {
            fetchNewsCounts: () => void;
            removeItem: (id: string, state: NewsFeedDTO['state']) => void;
          };
        }>(self);

        const currentState = self.state;
        void root.news.fetchNewsCounts();
        root.news.removeItem(self.id, currentState);
      } catch (error) {
        self.deleteStatus.errorStates();
        self.deleteStatus.finallyStates();
        console.error(error);

        return Promise.reject(error);
      }
    }),
  }));
